/**
 * Privacy Policy — PIPEDA-aligned disclosure for Nanny B's Bakery (Ontario, Canada).
 */

import { Link } from 'react-router-dom';
import { usePageSeo } from '../utils/seo';

const LAST_UPDATED = 'July 10, 2026';

export default function PrivacyPolicy() {
  usePageSeo({
    title: "Privacy Policy | Nanny B's Bakery — Barrie, ON",
    description:
      "How Nanny B's Bakery collects, uses, and protects your personal information under Canadian privacy law (PIPEDA). Cookies, orders, and contact details explained.",
    canonicalPath: '/privacy',
    keywords: "Nanny B bakery privacy policy, PIPEDA bakery Barrie, cookie policy Ontario",
  });

  return (
    <div className="bg-bakery-cream min-h-screen">
      <div className="container-custom section-padding max-w-3xl">
        <h1 className="text-3xl font-display font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-gray max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-semibold text-gray-900">Who we are</h2>
            <p>
              Nanny B&apos;s Bakery (&quot;we&quot;, &quot;us&quot;) operates{' '}
              <a href="https://nannybbakery.ca" className="text-primary-700 hover:underline">
                nannybbakery.ca
              </a>{' '}
              and our bakery at 62 Commerce Park Drive, Unit L, Barrie, Ontario. Questions about
              privacy:{' '}
              <a href="mailto:nannybbakedgoods@outlook.com" className="text-primary-700 hover:underline">
                nannybbakedgoods@outlook.com
              </a>{' '}
              or (705) 503-4966.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">Canadian privacy law</h2>
            <p>
              We follow Canada&apos;s <strong>Personal Information Protection and Electronic Documents Act (PIPEDA)</strong>.
              Ontario does not have a separate cookie law like Quebec&apos;s Law 25, but we still obtain
              meaningful consent before optional analytics and explain our practices clearly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">Information we collect</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Orders:</strong> name, phone, email, pickup date/time, order details, and notes
                you provide at checkout.
              </li>
              <li>
                <strong>Contact form &amp; messages:</strong> name, email, phone (if provided), and your message.
              </li>
              <li>
                <strong>Email list (optional):</strong> email address if you sign up for specials.
              </li>
              <li>
                <strong>Payment:</strong> card payments are processed by Square; we do not store full card numbers on our servers.
              </li>
              <li>
                <strong>Website usage (only if you accept analytics cookies):</strong> pages visited and
                general interaction data via Google Analytics and/or Meta Pixel, when those tools are enabled.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">Why we use it</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Fulfill pickup orders and custom cake requests.</li>
              <li>Respond to questions and send order-related communication.</li>
              <li>Send marketing emails only if you opted in (you can unsubscribe anytime).</li>
              <li>Improve our website when you consent to analytics.</li>
              <li>Comply with tax and business record requirements.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">Cookies &amp; similar technologies</h2>
            <p>
              <strong>Essential:</strong> We may use browser session storage to remember promo popups you
              closed during a visit. Our staff admin area uses a secure login session cookie for Tammy&apos;s
              portal only — not on the public shopping site.
            </p>
            <p>
              <strong>Analytics (optional):</strong> If you click &quot;Accept all&quot; on our cookie notice,
              we may load Google Analytics (GA4) and/or Meta (Facebook) Pixel to measure site traffic and
              ad performance. You can choose &quot;Essential only&quot; to decline these.
            </p>
            <p>
              <strong>Third-party links:</strong> Links to Uber Eats, Facebook, Instagram, or Messenger
              open those companies&apos; sites, which have their own privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">Sharing</h2>
            <p>We do not sell your personal information. We share data only as needed with:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Payment processor (Square) to complete transactions.</li>
              <li>Email delivery providers to send notifications you expect.</li>
              <li>Analytics providers (Google, Meta) only if you consented to analytics cookies.</li>
              <li>Authorities when required by law.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">Retention &amp; security</h2>
            <p>
              We keep order and contact records as long as needed for business, tax, and customer-service
              purposes, then delete or anonymize when no longer required. We use reasonable safeguards
              (secure hosting, access controls for staff tools).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">Your rights</h2>
            <p>
              You may ask what personal information we hold about you, request corrections, or withdraw
              consent for optional marketing/analytics. Contact us at{' '}
              <a href="mailto:nannybbakedgoods@outlook.com" className="text-primary-700 hover:underline">
                nannybbakedgoods@outlook.com
              </a>
              . You may also contact the{' '}
              <a
                href="https://www.priv.gc.ca/en/contact-the-opc/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-700 hover:underline"
              >
                Office of the Privacy Commissioner of Canada
              </a>{' '}
              about PIPEDA concerns.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">Children</h2>
            <p>
              Our site is a general bakery storefront. We do not knowingly collect personal information
              from children under 13 without parental consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">Changes</h2>
            <p>
              We may update this policy; the &quot;Last updated&quot; date will change. Continued use of the
              site after changes means you accept the updated policy.
            </p>
          </section>
        </div>

        <p className="mt-10 text-sm text-gray-500">
          <Link to="/" className="text-primary-700 hover:underline">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
